import React, {
  Children,
  isValidElement,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import type { Breakpoint, SxProps, Theme } from '@mui/material/styles';
import FilterListIcon from '@mui/icons-material/FilterList';
import FilterDrawerItem, {
  type FilterDrawerItemProps,
} from './FilterDrawerItem';
import FilterDrawerPanel from './FilterDrawerPanel';
import { FilterActivityProvider } from './FilterActivityContext';
import { containerStyles as styles } from './styles';

const asSxArray = (sx: SxProps<Theme> | undefined) =>
  Array.isArray(sx) ? sx : [sx];

const asFilterDrawerItem = (node: React.ReactNode) =>
  isValidElement<FilterDrawerItemProps>(node) && node.type === FilterDrawerItem
    ? node
    : undefined;

/** Inline sizing of a child wrapped in `FilterDrawerItem`, if any. */
const getItemInlineSx = (node: React.ReactNode): SxProps<Theme> | undefined =>
  asFilterDrawerItem(node)?.props.inlineSx;

const isPinnedItem = (node: React.ReactNode): boolean =>
  Boolean(asFilterDrawerItem(node)?.props.pinned);

/**
 * Props for FilterDrawerContainer component.
 */
export interface FilterDrawerContainerProps {
  /** Search field component to display in the top bar. */
  searchField?: React.ReactElement;
  /** Breakpoint below which filters collapse into drawer. Default: 'lg' (1200px). */
  collapseAt?: Breakpoint;
  /** Whether the filter drawer is currently open. */
  isDrawerOpen: boolean;
  /** Callback fired when user clicks the filter icon to open drawer. */
  onOpenDrawer: () => void;
  /** Callback fired when user closes the drawer (Cancel button or outside click). */
  onCloseDrawer: () => void;
  /** Callback fired when user clicks Save in the drawer. */
  onSave: () => void;
  /** Callback fired when user clicks Clear All button. */
  onClearAll: () => void;
  /** Whether to show the Clear All button. Default: false. */
  showClearAllButton?: boolean;
  /** Optional callback fired when collapsed state changes (viewport resize). */
  onCollapsedChange?: (collapsed: boolean) => void;
  /**
   * Content that ends the bar at every size, outside the drawer (e.g. the
   * Schedule/Standings toggle).
   */
  trailingContent?: React.ReactNode;
  /**
   * Filter components to render (inline on desktop, in drawer on mobile,
   * except `pinned` items, which stay in the bar).
   */
  children: React.ReactNode;
}

/**
 * Responsive filter bar.
 *
 * Above `collapseAt` (default 'lg' = 1200px) all filters render inline next to
 * the search field. Below it, the search field stays in the top bar next to a
 * filter icon button (with active-count badge), and the filters move into a
 * right-anchored drawer, except the `pinned` ones, which stay in the bar
 * before the filter icon. Inside the drawer the host page is expected to bind
 * its filter components to a *draft* state and only apply changes on Save —
 * this container only owns the chrome (open/close, Cancel, Save, Clear All).
 */
const FilterDrawerContainer: React.FC<FilterDrawerContainerProps> = ({
  searchField,
  collapseAt = 'lg',
  isDrawerOpen,
  onOpenDrawer,
  onCloseDrawer,
  onSave,
  onClearAll,
  showClearAllButton = false,
  onCollapsedChange,
  trailingContent,
  children,
}) => {
  const theme = useTheme();
  const isCollapsed = useMediaQuery(theme.breakpoints.down(collapseAt));

  const [activeFilterCount, setActiveFilterCount] = useState(0);

  const handleActivityCountChange = useCallback((count: number) => {
    setActiveFilterCount(count);
  }, []);

  useEffect(() => {
    onCollapsedChange?.(isCollapsed);
  }, [isCollapsed, onCollapsedChange]);

  useEffect(() => {
    // Close drawer if viewport widens past the breakpoint.
    if (!isCollapsed && isDrawerOpen) {
      onCloseDrawer();
    }
  }, [isCollapsed, isDrawerOpen, onCloseDrawer]);

  const childrenArray = useMemo(() => Children.toArray(children), [children]);

  const wrapChild = useCallback(
    (child: React.ReactNode, index: number, isInline: boolean) => (
      <Box
        // eslint-disable-next-line react/no-array-index-key
        key={index}
        // Inside the drawer (collapsed) the parent is a column flexbox —
        // each row must take its natural height, NOT flex-grow, otherwise
        // rows stretch to fill the drawer leaving big gaps between them.
        // Inline (desktop) the parent is a row flexbox — flex-grow lets
        // filters share the bar width.
        sx={
          isInline
            ? [
                ...asSxArray(styles.childBoxInline),
                ...asSxArray(getItemInlineSx(child)),
              ]
            : styles.childBoxCollapsed
        }
      >
        {child}
      </Box>
    ),
    []
  );

  const wrappedChildren = useMemo(
    () =>
      childrenArray.map((child, index) =>
        isCollapsed && isPinnedItem(child)
          ? null
          : wrapChild(child, index, !isCollapsed)
      ),
    [childrenArray, isCollapsed, wrapChild]
  );

  // Rendered outside the activity provider, so a filter that stays in view
  // does not count toward the badge on the filter icon.
  const pinnedChildren = useMemo(
    () =>
      isCollapsed
        ? childrenArray.map((child, index) =>
            isPinnedItem(child) ? wrapChild(child, index, true) : null
          )
        : null,
    [childrenArray, isCollapsed, wrapChild]
  );

  return (
    <Box sx={styles.outerBar}>
      {pinnedChildren}
      <FilterActivityProvider onCountChange={handleActivityCountChange}>
        {searchField && (
          <Box
            sx={[
              ...asSxArray(styles.searchFieldWrapper),
              ...asSxArray(
                isCollapsed ? undefined : getItemInlineSx(searchField)
              ),
            ]}
          >
            {searchField}
          </Box>
        )}

        {!isCollapsed && wrappedChildren}

        {showClearAllButton && !isCollapsed && (
          <Button
            onClick={onClearAll}
            color="secondary"
            variant="contained"
            size="large"
            data-testid="filter-clear-all"
            sx={styles.clearButton}
          >
            Clear
          </Button>
        )}

        {isCollapsed && (
          <Box sx={styles.iconButtonWrapper}>
            <IconButton
              aria-label="Open filters"
              data-testid="filter-drawer-open"
              onClick={onOpenDrawer}
              sx={styles.iconButton}
            >
              <FilterListIcon />
            </IconButton>
            {activeFilterCount > 0 && (
              <Box data-testid="filter-drawer-badge" sx={styles.badge}>
                {activeFilterCount}
              </Box>
            )}
          </Box>
        )}

        <FilterDrawerPanel
          isOpen={isCollapsed && isDrawerOpen}
          onClose={onCloseDrawer}
          onSave={onSave}
          onClearAll={onClearAll}
        >
          {/* Render children inside the drawer ONLY when collapsed. The drawer
            keeps its content mounted (see ModalProps.keepMounted in the panel)
            so filters stay alive while the drawer is closed and continue to
            report their activity to the registry. Without the isCollapsed
            guard, desktop view would mount the same filters twice (once in
            the outer bar, once in the always-mounted drawer body). */}
          {isCollapsed ? wrappedChildren : null}
        </FilterDrawerPanel>
      </FilterActivityProvider>
      {trailingContent}
    </Box>
  );
};

export default FilterDrawerContainer;
